import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { Head } from '@inertiajs/react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { HabitLayout, HabitForm, HabitLogModal } from '@/components/habits'
import { SatisfactionEmoji } from '@/components/satisfaction'
import { useHabitToggle } from '@/hooks/useHabitToggle'
import { cn } from '@/lib/utils'
import { formatDateLocal, getDaysInRange } from '@/lib/date'
import type { HabitsTimelineProps, Habit } from '@/types'

function formatShortDate(date: Date): string {
  return `${date.getMonth() + 1}/${date.getDate()}`
}

export default function HabitsTimeline({
  habits: initialHabits,
  startDate,
  endDate,
  today, // サーバーから提供される「今日」を使用
  satisfactions = {},
}: HabitsTimelineProps) {
  const [habits, setHabits] = useState(initialHabits)
  const [showForm, setShowForm] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // props の変更を監視してローカル状態を同期
  useEffect(() => {
    setHabits(initialHabits)
  }, [initialHabits])

  const days = useMemo(
    () => getDaysInRange(startDate, endDate),
    [startDate, endDate]
  )

  const {
    toggle,
    isPending,
    logModalTarget,
    closeLogModal,
    saveLog,
    uncomplete,
  } = useHabitToggle({
    onError: (error) => console.error('Toggle failed:', error),
  })

  // Scroll to today (end) on mount
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth
    }
  }, [])

  const isCompleted = useCallback((habit: Habit, date: Date) => {
    const dateStr = formatDateLocal(date)
    return habit.completedDates?.includes(dateStr) ?? false
  }, [])

  const isScheduled = useCallback((habit: Habit, date: Date) => {
    if (habit.frequencyType === 'daily') return true
    return habit.frequencyDays.includes(date.getDay())
  }, [])

  const calculateAchievementRate = useCallback(
    (habit: Habit, completedDates: string[]) => {
      const scheduledCount = days.filter((day) =>
        isScheduled(habit, day)
      ).length
      if (scheduledCount === 0) return 0
      // 全完了日をカウント（予定内外問わず）→ 100%超え可能
      const completedCount = completedDates.filter((dateStr) =>
        days.some((day) => formatDateLocal(day) === dateStr)
      ).length
      return Math.round((completedCount / scheduledCount) * 100)
    },
    [days, isScheduled]
  )

  const handleToggle = useCallback(
    (habitId: number, date: Date) => {
      const habit = habits.find((h) => h.id === habitId)
      if (!habit) return

      const dateStr = formatDateLocal(date)
      const completed = isCompleted(habit, date)

      toggle(
        habitId,
        dateStr,
        completed,
        (newCompleted) => {
          setHabits((prev) =>
            prev.map((h) => {
              if (h.id !== habitId) return h
              const newDates = newCompleted
                ? [...(h.completedDates || []), dateStr]
                : (h.completedDates || []).filter((d) => d !== dateStr)
              const newRate = calculateAchievementRate(h, newDates)
              return {
                ...h,
                completedDates: newDates,
                achievementRate: newRate,
              }
            })
          )
        },
        {
          enabled: !!habit.loggingEnabled,
          habitName: habit.name,
          existingLog: habit.logDetails?.[dateStr],
        }
      )
    },
    [habits, isCompleted, toggle, calculateAchievementRate]
  )

  return (
    <>
      <Head title="タイムライン | Habit Tracker" />

      <HabitLayout
        title="タイムライン（30日）"
        action={
          <Button onClick={() => setShowForm(true)} className="touch-target">
            <Plus className="mr-1 h-5 w-5" />
            <span className="hidden sm:inline">新しい習慣</span>
          </Button>
        }
      >
        {habits.length > 0 ? (
          <div className="bg-card border-border overflow-hidden rounded-xl border">
            <div className="flex">
              {/* Fixed habit names column */}
              <div className="border-border bg-card z-10 w-32 flex-shrink-0 border-r sm:w-44">
                {/* Header spacer */}
                <div className="border-border flex h-12 items-center justify-between border-b px-3">
                  <span className="text-muted-foreground text-xs font-medium">
                    習慣
                  </span>
                  <span className="text-muted-foreground text-xs font-medium">
                    達成率
                  </span>
                </div>

                {/* Habit names */}
                {habits.map((habit) => (
                  <div
                    key={habit.id}
                    className="border-border flex h-12 items-center justify-between border-b px-3"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {habit.name}
                    </span>
                    <span
                      className={cn(
                        'ml-2 flex-shrink-0 text-xs font-medium',
                        (habit.achievementRate ?? 0) >= 100
                          ? 'text-green-600'
                          : (habit.achievementRate ?? 0) >= 80
                            ? 'text-green-600'
                            : (habit.achievementRate ?? 0) >= 50
                              ? 'text-yellow-600'
                              : 'text-muted-foreground'
                      )}
                    >
                      {habit.achievementRate ?? 0}%
                      {(habit.achievementRate ?? 0) > 100 && '★'}
                    </span>
                  </div>
                ))}
                {/* 気分行 */}
                <div className="border-border flex h-12 items-center justify-between border-b px-3">
                  <span className="text-muted-foreground text-xs font-medium">
                    気分
                  </span>
                </div>
              </div>

              {/* Scrollable dates area */}
              <div
                ref={scrollRef}
                className="scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent flex-1 overflow-x-auto"
              >
                <div className="inline-flex min-w-full flex-col">
                  {/* Date headers */}
                  <div className="border-border bg-muted/30 flex h-12 border-b">
                    {days.map((day) => {
                      const dateStr = formatDateLocal(day)
                      const isToday = dateStr === today

                      return (
                        <div
                          key={dateStr}
                          className={cn(
                            'flex w-10 flex-shrink-0 flex-col items-center justify-center text-xs',
                            isToday && 'bg-primary/10'
                          )}
                        >
                          <span
                            className={cn(
                              'font-medium',
                              isToday ? 'text-primary' : 'text-muted-foreground'
                            )}
                          >
                            {formatShortDate(day)}
                          </span>
                        </div>
                      )
                    })}
                  </div>

                  {/* Satisfaction row */}
                  <div className="border-border flex h-12 border-b">
                    {days.map((day) => {
                      const dateStr = formatDateLocal(day)
                      const isToday = dateStr === today
                      return (
                        <div
                          key={`sat-${dateStr}`}
                          className={cn(
                            'flex w-10 flex-shrink-0 items-center justify-center',
                            isToday && 'bg-primary/5'
                          )}
                        >
                          <SatisfactionEmoji
                            level={satisfactions[dateStr] ?? null}
                            size="sm"
                          />
                        </div>
                      )
                    })}
                  </div>

                  {/* Habit rows */}
                  {habits.map((habit) => (
                    <div
                      key={habit.id}
                      className="border-border flex h-12 border-b"
                    >
                      {days.map((day) => {
                        const dateStr = formatDateLocal(day)
                        const scheduled = isScheduled(habit, day)
                        const completed = isCompleted(habit, day)
                        const extra = !scheduled // 予定外日
                        const pending = isPending(habit.id, dateStr)
                        const isToday = dateStr === today
                        const habitColor = habit.color || '#3b82f6'

                        return (
                          <div
                            key={dateStr}
                            className={cn(
                              'flex w-10 flex-shrink-0 items-center justify-center',
                              isToday && 'bg-primary/5'
                            )}
                          >
                            <button
                              onClick={() => handleToggle(habit.id, day)}
                              disabled={pending}
                              className={cn(
                                'timeline-cell',
                                extra && !completed && 'extra',
                                pending && 'animate-pulse'
                              )}
                              style={
                                completed
                                  ? extra
                                    ? {
                                        // 予定外完了: 薄い背景 + 点線枠
                                        backgroundColor: `${habitColor}b3`,
                                        borderColor: habitColor,
                                        borderWidth: '2px',
                                        borderStyle: 'dashed',
                                      }
                                    : {
                                        // 予定日完了: 塗りつぶし
                                        backgroundColor: habitColor,
                                      }
                                  : extra
                                    ? {
                                        // 予定外未完了: グレー点線枠
                                        borderColor: '#9ca3af',
                                        borderWidth: '1px',
                                        borderStyle: 'dashed',
                                      }
                                    : {
                                        // 予定日未完了: 実線枠
                                        borderColor: habitColor,
                                        borderWidth: '2px',
                                      }
                              }
                              aria-label={
                                extra
                                  ? completed
                                    ? '追加完了済み'
                                    : '予定外（クリックで追加完了）'
                                  : completed
                                    ? '完了済み'
                                    : '未完了'
                              }
                            />
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="bg-muted/30 border-border text-muted-foreground flex flex-wrap items-center gap-4 border-t p-3 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="bg-primary h-4 w-4 rounded-full" />
                <span>完了</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="bg-muted border-muted-foreground/20 h-4 w-4 rounded-full border-2" />
                <span>未完了</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div
                  className="h-4 w-4 rounded-full"
                  style={{
                    backgroundColor: '#3b82f6b3',
                    border: '2px dashed #3b82f6',
                  }}
                />
                <span>追加完了</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div
                  className="h-4 w-4 rounded-full"
                  style={{
                    border: '1px dashed #9ca3af',
                  }}
                />
                <span>予定外</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-12 text-center">
            <p className="text-muted-foreground mb-4">習慣がまだありません</p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="mr-1 h-5 w-5" />
              習慣を追加
            </Button>
          </div>
        )}
      </HabitLayout>

      {/* Form Modal */}
      {showForm && <HabitForm onClose={() => setShowForm(false)} />}

      {/* Log Modal */}
      {logModalTarget && (
        <HabitLogModal
          habitName={logModalTarget.habitName}
          initialData={logModalTarget.existingLog}
          mode={logModalTarget.mode}
          onSave={(data) =>
            saveLog(logModalTarget.habitId, logModalTarget.date, data)
          }
          onSkip={closeLogModal}
          onUncomplete={
            logModalTarget.mode === 'edit'
              ? () => {
                  const { habitId, date } = logModalTarget
                  uncomplete(habitId, date, () => {
                    setHabits((prev) =>
                      prev.map((h) => {
                        if (h.id !== habitId) return h
                        const newDates = (h.completedDates || []).filter(
                          (d) => d !== date
                        )
                        const newRate = calculateAchievementRate(h, newDates)
                        return {
                          ...h,
                          completedDates: newDates,
                          achievementRate: newRate,
                        }
                      })
                    )
                  })
                }
              : undefined
          }
        />
      )}
    </>
  )
}
